import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const RefundModal = ({
  isOpen,
  onClose,
  annualSaving,
  onNavigate,
  onRestartFlow,
}) => {
  const navigate = useNavigate();
  const canSave = annualSaving >= 200;
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  const handleChangeAnswers = () => {
    console.log("🔍 Change my answers clicked");
    console.log("🔍 onRestartFlow function:", typeof onRestartFlow);

    // Close the modal first
    onClose();

    // If onRestartFlow function is provided, use it to reset the current flow
    if (onRestartFlow && typeof onRestartFlow === "function") {
      console.log("🔍 Calling onRestartFlow function");
      // Small delay to ensure modal closes first
      setTimeout(() => {
        onRestartFlow();
      }, 100);
    } else {
      console.log("🔍 onRestartFlow not available, using navigation fallback");
      // Fallback: Navigate back to the savings flow
      navigate(`/preview/flow/688a0e2564d03f7782f0910a`, { replace: true });
    }
  };

  const formatAmount = (amount) => {
    return amount ? `£${amount.toLocaleString()}` : "£0";
  };

  if (!isOpen && !isVisible) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleButtonClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    // Add ripple effect
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add("ripple");

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    if (action === "change") {
      handleChangeAnswers();
    } else if (action === "navigate") {
      onNavigate();
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-all duration-500 ease-out ${
          isVisible ? "opacity-100" : "opacity-0"
        } ${isOpen ? "bg-opacity-30" : "bg-opacity-0"}`}
        onClick={handleBackdropClick}
      >
        <div
          className={`bg-white rounded-3xl max-w-md w-full mx-4 px-6 sm:px-8 py-8 sm:py-10 text-center shadow-2xl relative transform transition-all duration-500 ease-out ${
            isAnimating && isOpen
              ? "scale-100 opacity-100 translate-y-0"
              : "scale-90 opacity-0 translate-y-8"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated background gradient */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-[#8AB9F1] via-[#B57EDC] to-[#F08080] rounded-full opacity-10 blur-3xl animate-pulse"></div>
            <div
              className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-[#F08080] via-[#B57EDC] to-[#8AB9F1] rounded-full opacity-10 blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          {/* No Refund Message */}
          <h2
            className={`text-xl font-semibold text-[#141A33] mb-6 transition-all duration-700 delay-100 ease-out relative z-10 ${
              isAnimating
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
          >
            Based on your answers we don't think you can get a refund from HMRC
            for past years.
          </h2>

          {canSave ? (
            // Can save money scenario
            <>
              {/* Dynamic amount section */}
              <div className="relative flex flex-col items-center mb-6 z-10">
                <h3
                  className={`text-2xl font-bold text-[#141A33] mb-2 transition-all duration-700 delay-200 ease-out ${
                    isAnimating
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
                >
                  But you can save
                </h3>
                <p
                  className={`text-5xl sm:text-6xl font-extrabold bg-gradient-to-r from-[#8AB9F1] via-[#B57EDC] to-[#F08080] text-transparent bg-clip-text mb-2 transition-all duration-700 delay-300 ease-out ${
                    isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  }`}
                >
                  {formatAmount(annualSaving)}
                </p>
                <span
                  className={`text-gray-500 text-sm transition-all duration-700 delay-400 ease-out ${
                    isAnimating ? "opacity-100" : "opacity-0"
                  }`}
                >
                  a year with Savy from now on
                </span>
              </div>

              {/* Button */}
              <button
                onClick={(e) => handleButtonClick(e, "navigate")}
                className={`w-full bg-[#002147] text-white py-3.5 rounded-xl font-semibold hover:bg-[#001730] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden z-10 ${
                  isAnimating
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: isAnimating ? "500ms" : "0ms" }}
              >
                <span className="relative z-10">Next →</span>
              </button>
            </>
          ) : (
            // Cannot save money scenario
            <>
              <h3
                className={`text-xl font-semibold text-[#141A33] mb-8 transition-all duration-700 delay-200 ease-out relative z-10 ${
                  isAnimating
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
              >
                Based on your answers we don't think you can save money with
                Savy.
              </h3>

              {/* Button */}
              <button
                onClick={(e) => handleButtonClick(e, "change")}
                className={`w-full bg-[#002147] text-white py-3.5 rounded-xl font-semibold hover:bg-[#001730] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden z-10 ${
                  isAnimating
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: isAnimating ? "500ms" : "0ms" }}
              >
                <span className="relative z-10">Change my answers</span>
              </button>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        .ripple {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.6);
          transform: scale(0);
          animation: ripple 600ms ease-out;
          pointer-events: none;
          z-index: 1;
        }
      `}</style>
    </>
  );
};

export default RefundModal;
